import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { toast } from "sonner";
import type { ResolvedItemLink, LinkType } from "@/types/itemLinks";

/**
 * Fetch all links for a specific backlog item.
 * Queries both directions (as source and as target) and resolves
 * the linked item data for display.
 */
export const useBacklogItemLinks = (itemId: string | null) => {
  return useQuery<ResolvedItemLink[]>({
    queryKey: ["backlog-item-links", itemId],
    queryFn: async () => {
      if (!itemId) return [];

      const { data: asSource, error: sourceError } = await supabase
        .from("backlog_item_links")
        .select("id, source_item_id, target_item_id, link_type, created_at, created_by_id")
        .eq("source_item_id", itemId);

      if (sourceError) throw sourceError;

      const { data: asTarget, error: targetError } = await supabase
        .from("backlog_item_links")
        .select("id, source_item_id, target_item_id, link_type, created_at, created_by_id")
        .eq("target_item_id", itemId);

      if (targetError) throw targetError;

      const allLinks = [...(asSource || []), ...(asTarget || [])];
      if (allLinks.length === 0) return [];

      // Collect all unique linked item IDs
      const linkedItemIds = new Set<string>();
      allLinks.forEach((link) => {
        if (link.source_item_id !== itemId) linkedItemIds.add(link.source_item_id);
        if (link.target_item_id !== itemId) linkedItemIds.add(link.target_item_id);
      });

      const { data: linkedItems, error: itemsError } = await supabase
        .from("backlog_items")
        .select(
          "id, row_number, ticket_number, task, status_id, priority_id, assigned_to_id, added_by_id, created_at, updated_at, deleted_at, ticket_type_id, team_ids, assigned_team_id",
        )
        .in("id", Array.from(linkedItemIds));

      if (itemsError) throw itemsError;

      const itemMap = new Map((linkedItems || []).map((item) => [item.id, item]));

      return allLinks
        .map((link) => {
          const isSource = link.source_item_id === itemId;
          const linkedItemId = isSource ? link.target_item_id : link.source_item_id;
          const linkedItem = itemMap.get(linkedItemId);

          let displayType: ResolvedItemLink["displayType"];
          if (link.link_type === "related_to") {
            displayType = "related_to";
          } else {
            displayType = isSource ? "blocks" : "blocked_by";
          }

          return {
            linkId: link.id,
            linkType: link.link_type as LinkType,
            displayType,
            linkedItem:
              linkedItem ||
              ({
                id: linkedItemId,
                row_number: 0,
                ticket_number: 0,
                task: "(Deleted item)",
                status_id: null,
                priority_id: null,
                assigned_to_id: null,
                added_by_id: "",
                created_at: "",
                updated_at: "",
                deleted_at: null,
                ticket_type_id: null,
              } as ResolvedItemLink["linkedItem"]),
          };
        })
        .filter((link) => !link.linkedItem.deleted_at);
    },
    enabled: !!itemId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch link counts for all items (used for badge display in BacklogRow).
 * Returns a Map<itemId, count>.
 */
export const useAllItemLinkCounts = () => {
  return useQuery<Map<string, number>>({
    queryKey: ["backlog-item-link-counts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("backlog_item_links").select("source_item_id, target_item_id");

      if (error) throw error;

      const counts = new Map<string, number>();
      (data || []).forEach((link) => {
        counts.set(link.source_item_id, (counts.get(link.source_item_id) || 0) + 1);
        counts.set(link.target_item_id, (counts.get(link.target_item_id) || 0) + 1);
      });
      return counts;
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Create a new item link.
 */
export const useCreateItemLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceItemId,
      targetItemId,
      linkType,
    }: {
      sourceItemId: string;
      targetItemId: string;
      linkType: LinkType;
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("backlog_item_links")
        .insert({
          source_item_id: sourceItemId,
          target_item_id: targetItemId,
          link_type: linkType,
          created_by_id: user.id,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error("This link already exists");
        }
        throw error;
      }
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["backlog-item-links", variables.sourceItemId] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item-links", variables.targetItemId] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item-link-counts"] });
      toast.success("Item linked successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create link");
    },
  });
};

/**
 * Delete an item link.
 */
export const useDeleteItemLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      linkId,
      sourceItemId,
      targetItemId,
    }: {
      linkId: string;
      sourceItemId: string;
      targetItemId: string;
    }) => {
      const { error } = await supabase.from("backlog_item_links").delete().eq("id", linkId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["backlog-item-links", variables.sourceItemId] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item-links", variables.targetItemId] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item-link-counts"] });
      toast.success("Link removed");
    },
    onError: () => {
      toast.error("Failed to remove link");
    },
  });
};
